import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  Divider,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import { ADD_MANUAL_RISK } from "../../../../../../apis/organization-apis/risk-apis/riskApis";
import { GET_CONTROLS_WITH_CHECKS } from "../../../../../../apis/settingsApis/framework-apis/frameworkApis";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../../../../apis/common-apis/commonApis";
import { GET_STAFF_DETAILS } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import { steps, ManualAddRiskSteps } from "./ManualAddRiskSteps";
import { MasterLookupItemsType } from "../../../../../../utils/enums";
import Role from "../../../../../../utils/roles";

export default function ManualAddRiskDialog({
  open,
  onClose,
  onAddRisks,
  setSnackbarInfo,
}) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    risk_owner_email: "",
    category_id: "",
    inherent_impact: "",
    inherent_likelihood: "",
  });
  const [errors, setErrors] = useState({});
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [mappedControls, setMappedControls] = useState([]);
  const [adminEmails, setAdminEmails] = useState([]);

  const {
    data: controlsApiData,
    loading: controlsLoading,
    error: controlsError,
  } = useQuery(GET_CONTROLS_WITH_CHECKS);

  const controlsData = (controlsApiData?.getControlsWithChecks?.data || []).map(
    (control) => ({
      controls_id: control?.controlsId,
      controls_name: control?.controlsName,
      controls_description: control?.controlsDescription,
      control_domain: control?.controlsDomain,
      control_title: control?.controlsTitle,
      associated_checks: (control?.checks || []).map((check) => ({
        active_check_id: check?.activeCheckId,
        active_check_name: check?.activeCheckName,
        active_check_description: check?.activeCheckDescription,
        check_type: check?.checkType,
        monitor_type: check?.monitorType,
        active_check_category: check?.activeCheckCategory,
      })),
    })
  );

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(GET_STAFF_DETAILS);

  useEffect(() => {
    if (staffData) {
      const adminsEmailList = staffData?.getEmployees?.data
        ?.filter((employee) =>
          employee?.roles?.filter(
            (role) => role?.role_description === Role.ADMIN
          )
        )
        .map((employee) => employee?.emails?.[0]?.email);
      console.log("Admins Email List:", adminsEmailList);

      setAdminEmails(adminsEmailList);
    }
  }, [staffData]);

  const { data: riskCategoriesData, loading: riskCategoriesLoading } = useQuery(
    GET_MASTER_LOOKUP_BY_TYPE,
    {
      variables: {
        type: MasterLookupItemsType.RISK_CATEGORY,
      },
    }
  );

  const riskCategories = (
    riskCategoriesData?.getMasterLookupByType?.data || []
  ).map((cat) => ({ value: cat?.id, label: cat?.title }));

  const domains = Array.from(
    new Set(controlsData?.map((c) => c?.control_domain)?.filter(Boolean))
  );

  const mappedCount = mappedControls?.length;
  const selectedChecksCount = selectedChecks?.length;

  const calculateScore = () => {
    const impact = parseInt(formData?.inherent_impact) || 1;
    const likelihood = parseInt(formData?.inherent_likelihood) || 1;
    return impact * likelihood;
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData?.name?.trim()) newErrors.name = "Risk name is required";
        if (!formData?.description?.trim())
          newErrors.description = "Risk description is required";
        if (!formData?.risk_owner_email?.trim())
          newErrors.risk_owner_email = "Risk owner email is required";
        if (!formData?.category_id)
          newErrors.category_id = "Risk category is required";
        if (!formData?.inherent_impact)
          newErrors.inherent_impact = "Inherent impact is required";
        if (!formData?.inherent_likelihood)
          newErrors.inherent_likelihood = "Inherent likelihood is required";
        break;
      case 1:
        if (selectedChecks?.length === 0) {
          newErrors.checks = "At least one check must be selected";
        }
        break;
      default:
        // No validation for other steps
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper to update both selectedChecks and mappedControls
  const updateSelectedChecksAndMappedControls = (newSelected) => {
    setSelectedChecks(newSelected);
    const newMappedControls = [];
    controlsData?.forEach((control) => {
      const selectedControlChecks = (control?.associated_checks || [])?.filter(
        (check) => newSelected?.includes(check?.active_check_id)
      );
      if (selectedControlChecks?.length > 0) {
        newMappedControls.push({
          ...control,
          associated_checks: selectedControlChecks,
        });
      }
    });
    setMappedControls(newMappedControls);
  };

  const handleCheckToggle = (checkId, control) => {
    let newSelected;
    if (selectedChecks?.includes(checkId)) {
      newSelected = selectedChecks?.filter((id) => id !== checkId);
    } else {
      newSelected = [...(selectedChecks || []), checkId];
    }
    updateSelectedChecksAndMappedControls(newSelected);
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const [addManualRisk, { loading: addRiskLoading }] =
    useMutation(ADD_MANUAL_RISK);

  const handleSubmit = async () => {
    if (validateStep(activeStep)) {
      const inherent_impact = parseInt(formData?.inherent_impact);
      const inherent_likelihood = parseInt(formData?.inherent_likelihood);

      const check_control_mapping = [];
      const mappingSet = new Set();
      mappedControls?.forEach((control) => {
        (control?.associated_checks || [])?.forEach((check) => {
          if (selectedChecks?.includes(check?.active_check_id)) {
            const key = `${check?.active_check_id}`;
            if (!mappingSet.has(key)) {
              check_control_mapping.push({
                active_check_id: check?.active_check_id,
                controls_id: null,
              });
              mappingSet.add(key);
            }
          }
        });
      });

      try {
        const { data } = await addManualRisk({
          variables: {
            title: formData?.name?.trim(),
            description: formData?.description?.trim(),
            risk_owner_email: formData?.risk_owner_email?.trim(),
            risk_category_id: parseInt(formData?.category_id),
            inherent_impact,
            inherent_likelihood,
            check_control_mappings: check_control_mapping,
          },
        });
        if (data?.addRiskManually?.success) {
          setSnackbarInfo({
            message: data?.addRiskManually?.message,
            vertical: "top",
            horizontal: "right",
          });
          handleClose();
          onAddRisks?.();
        } else {
          setSnackbarInfo({
            message: data?.addRiskManually?.message || "Failed to add risk",
            vertical: "top",
            horizontal: "right",
          });
        }
      } catch (err) {
        setSnackbarInfo({
          message: "Error adding risk",
          vertical: "top",
          horizontal: "right",
        });
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      risk_owner_email: "",
      category_id: "",
      inherent_impact: "",
      inherent_likelihood: "",
    });
    setErrors({});
    setSelectedChecks([]);
    setMappedControls([]);
    setActiveStep(0);
    onClose();
  };

  const calculatedScore = calculateScore();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        Add Risk Manually
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.customColors.text.secondary,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ background: theme.customColors.background.main }}
      >
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: theme.customColors.background.info,
            border: `1px solid ${theme.customColors.border.accent}`,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: "14px", color: theme.customColors.text.strong }}
          >
            Create a custom risk with your own assessment criteria and specific
            requirements. Follow the steps below to complete the risk creation
            process.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              "& .MuiStepLabel-root .Mui-completed": {
                color: theme.customColors.status.success.main,
              },
              "& .MuiStepLabel-root .Mui-active": {
                color: theme.customColors.button.dark,
              },
              "& .MuiStepLabel-label": {
                fontSize: "13px",
                fontWeight: 600,
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider sx={{ mb: 3, borderColor: theme.customColors.border.main }} />

        <ManualAddRiskSteps
          step={activeStep}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          selectedChecks={selectedChecks}
          mappedControls={mappedControls}
          controlsData={controlsData}
          riskCategories={riskCategories}
          controlsLoading={controlsLoading}
          controlsError={controlsError}
          updateSelectedChecksAndMappedControls={
            updateSelectedChecksAndMappedControls
          }
          handleCheckToggle={handleCheckToggle}
          riskCategoriesLoading={riskCategoriesLoading}
          calculatedScore={calculatedScore}
          mappedCount={mappedCount}
          selectedChecksCount={selectedChecksCount}
          adminEmails={adminEmails}
          staffLoading={staffLoading}
        />
      </DialogContent>

      <DialogActions
        sx={{
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            fontSize: "14px",
            color: theme.customColors.text.secondary,
            borderColor: theme.customColors.border.main,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
          disabled={addRiskLoading}
        >
          Cancel
        </Button>

        <Box sx={{ display: "flex", gap: 2 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              variant="outlined"
              sx={{
                fontSize: "14px",
                color: theme.customColors.text.secondary,
                borderColor: theme.customColors.border.main,
                "&:hover": {
                  backgroundColor: theme.customColors.background.hover,
                  color: theme.customColors.text.strong,
                },
              }}
              disabled={addRiskLoading}
            >
              Back
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Button
              onClick={handleSubmit}
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontSize: "14px",
                backgroundColor: theme.customColors.button.dark,
                color: theme.customColors.button.text,
                "&:hover": { backgroundColor: theme.customColors.button.dark },
              }}
              disabled={addRiskLoading}
            >
              {addRiskLoading ? "Creating..." : "Create Risk"}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{
                fontSize: "14px",
                backgroundColor: theme.customColors.button.dark,
                color: theme.customColors.button.text,
                "&:hover": { backgroundColor: theme.customColors.button.hover },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
